import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult, connectAuthEmulator } from "firebase/auth";

// Get the current domain for proper Firebase configuration
const currentDomain = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
const currentPort = typeof window !== 'undefined' ? window.location.port : '5000';
const currentOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5000';

// Ensure we have all Firebase environment variables
// Try multiple sources in this order:
// 1. Environment variables
// 2. LocalStorage fallbacks
// 3. Hardcoded defaults

// Helper to get from localStorage with fallback
const getFromStorage = (key: string, fallback: string): string => {
  if (typeof window === 'undefined') return fallback;
  return localStorage.getItem(key) || fallback;
};

// Check for global configuration fallback
const getGlobalFirebaseConfig = (): Record<string, string> | null => {
  if (typeof window === 'undefined') return null;
  
  // @ts-ignore - check for global config set by pre-boot
  return window.__FIREBASE_CONFIG__ || null;
};

const globalConfig = getGlobalFirebaseConfig();

// Use multiple fallback mechanisms to ensure Firebase works even if env vars are missing
// Priority: 1. Environment variables, 2. Global config, 3. LocalStorage, 4. Hardcoded defaults
const FIREBASE_API_KEY = import.meta.env.VITE_FIREBASE_API_KEY || 
                         (globalConfig?.apiKey) ||
                         getFromStorage('staymiddle_fallback_firebase_api_key', 'AIzaSyBiFd8SoSzG_ceZ48yKM8uSaPNAK2CrNHs');

const FIREBASE_PROJECT_ID = import.meta.env.VITE_FIREBASE_PROJECT_ID || 
                           (globalConfig?.projectId) ||
                           getFromStorage('staymiddle_fallback_firebase_project_id', 'staymiddle-com');

const FIREBASE_APP_ID = import.meta.env.VITE_FIREBASE_APP_ID || 
                        (globalConfig?.appId) ||
                        getFromStorage('staymiddle_fallback_firebase_app_id', '1:289792559879:web:1f2efddb14ee0f5ee093a2');

// Firebase configuration with fallbacks to prevent crashes
const firebaseConfig = {
  apiKey: FIREBASE_API_KEY,
  authDomain: `${FIREBASE_PROJECT_ID}.firebaseapp.com`,
  projectId: FIREBASE_PROJECT_ID,
  storageBucket: `${FIREBASE_PROJECT_ID}.appspot.com`,
  appId: FIREBASE_APP_ID,
  measurementId: "G-measurement-id"
};

// Log Firebase config for debugging (without the API key)
console.log("Firebase config (without API key):", {
  ...firebaseConfig,
  apiKey: "***REDACTED***",
  currentDomain,
  currentPort,
  currentOrigin
});

// Output warnings for missing environment variables in production
if (import.meta.env.PROD) {
  if (!import.meta.env.VITE_FIREBASE_API_KEY) {
    console.error("WARNING: Missing VITE_FIREBASE_API_KEY in production environment!");
  }
  if (!import.meta.env.VITE_FIREBASE_PROJECT_ID) {
    console.error("WARNING: Missing VITE_FIREBASE_PROJECT_ID in production environment!");
  }
  if (!import.meta.env.VITE_FIREBASE_APP_ID) {
    console.error("WARNING: Missing VITE_FIREBASE_APP_ID in production environment!");
  }
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// Connect to the auth emulator in development
if (currentDomain === 'localhost') {
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  console.log("Connected to Firebase Auth Emulator");
}

const googleProvider = new GoogleAuthProvider();

// Configure Google provider with specific OAuth client ID and parameters
googleProvider.setCustomParameters({
  prompt: 'select_account',
  client_id: '289792559879-a0it28md9jelqrm6skb4i0qti7j8m79f.apps.googleusercontent.com',
  // Add the current origin to help with OAuth verification
  origin: currentOrigin,
  // Add additional scopes if needed
  scope: 'profile email'
});

// Google Sign-in function
export const signInWithGoogle = async (redirect = false) => {
  try {
    console.log("Starting Google sign-in process...");
    console.log("Current origin:", currentOrigin);
    console.log("Auth domain:", firebaseConfig.authDomain);
    
    // Always use popup for better error reporting and compatibility
    const result = await signInWithPopup(auth, googleProvider);
    console.log("Google sign-in successful!");
    
    // Log the user details (without sensitive information)
    console.log("User signed in:", {
      uid: result.user.uid,
      email: result.user.email,
      displayName: result.user.displayName,
      photoURL: result.user.photoURL
    });
    
    return result;
  } catch (error: any) {
    console.error("Error during Google sign-in:", error);
    
    // Provide more helpful error messages
    if (error.code === 'auth/unauthorized-domain') {
      throw new Error(
        `This domain (${currentOrigin}) is not authorized for Google Sign-In. ` +
        'Please add it to the authorized domains in the Google Cloud Console.'
      );
    }
    
    if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Sign-in was cancelled by the user');
    }
    
    if (error.code === 'auth/cancelled-popup-request') {
      throw new Error('Another sign-in attempt is in progress');
    }
    
    throw error;
  }
};

// Handle redirect result
export const handleGoogleRedirectResult = async () => {
  try {
    console.log("Checking for Google sign-in redirect result...");
    const result = await getRedirectResult(auth);
    
    if (result) {
      console.log("Google sign-in redirect successful!");
      // Log the user details (without sensitive information)
      console.log("User signed in:", {
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL
      });
    }
    
    return result;
  } catch (error: any) {
    console.error("Error handling Google redirect:", error);
    throw error;
  }
};

// Export for use in other components
export { auth, googleProvider };